import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { UserPlus, Trash2, Copy, Check, Users, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Per-client Access section — operator UI over the /api/clients/{id}/access
 * endpoints. Lists bound emails, lets the operator add or revoke.
 *
 * IMPORTANT: this list is scoped strictly to the given `clientId`. It is NOT
 * the Google Login Allowlist (which is global and controls WHO MAY LOG IN
 * via Google). This list controls WHICH DATA a signed-in user may see.
 */
export const ClientAccessSection = ({ clientId, clientName, API }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [justBound, setJustBound] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/clients/${clientId}/access`);
      setRows(res.data.access || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load access list');
    } finally {
      setLoading(false);
    }
  }, [clientId, API]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/clients/${clientId}/access`, { email: em });
      setJustBound(em);
      setEmail('');
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Add failed');
    } finally { setBusy(false); }
  };

  const revoke = async (em) => {
    setBusy(true); setError('');
    try {
      await axios.delete(`${API}/clients/${clientId}/access/${encodeURIComponent(em)}`);
      if (justBound === em) setJustBound(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Revoke failed');
    } finally { setBusy(false); }
  };

  const stateLabel = (row) => {
    const provider = (row.auth_provider || '').toLowerCase();
    if (provider === 'pending') {
      return { text: 'Invited — not signed up yet', tone: 'bg-amber-50 text-amber-800 border-amber-200' };
    }
    if (provider === 'google') {
      return { text: 'Active — Google', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
    }
    if (provider === 'password') {
      return { text: 'Active — password', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
    }
    return { text: `Active — ${provider || 'unknown'}`, tone: 'bg-slate-50 text-slate-700 border-slate-200' };
  };

  const inviteText = justBound
    ? `Hi — I've given you access to ${clientName || 'the restaurant'} on Epicurea Nexus.\n\nGo to ${window.location.origin}/signup, sign up with the email ${justBound}, and choose a password. You'll land on your dashboard.`
    : '';

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) { /* noop */ }
  };

  return (
    <div className="border-t border-border pt-5 mt-5" data-testid="client-access-section">
      <div className="flex items-center gap-2 mb-1">
        <Users className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-primary">Access</h3>
      </div>
      <p className="text-xs text-text-muted mb-3">
        Emails bound below can sign up at <code>/signup</code> and will see ONLY <strong>{clientName || 'this client'}</strong>&apos;s data.
        Separate from the global Google Login Allowlist (Admin → Google Allowlist), which controls who may log in with Google.
      </p>

      <form onSubmit={submit} className="flex gap-2 mb-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20"
          placeholder="owner@yourrestaurant.com"
          data-testid="client-access-email-input"
        />
        <Button
          type="submit"
          disabled={busy || !email.trim()}
          className="bg-primary hover:bg-primary-hover text-white"
          data-testid="client-access-add-button"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1" /> Add</>}
        </Button>
      </form>

      {error && (
        <div className="bg-red-50 border-l-4 border-state-error rounded-md p-2 text-xs text-state-error flex items-start gap-2 mb-3" data-testid="client-access-error">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {justBound && inviteText && (
        <div className="bg-primary/5 border border-primary/20 rounded-md p-3 mb-3 text-xs" data-testid="client-access-invite-snippet">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            <strong className="text-primary">Copyable invite message for {justBound}</strong>
          </div>
          <pre className="whitespace-pre-wrap text-text-secondary font-sans text-xs mb-2">{inviteText}</pre>
          <button
            type="button"
            onClick={copyInvite}
            className="inline-flex items-center gap-1 text-primary hover:underline font-semibold"
            data-testid="client-access-copy-invite"
          >
            {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy to clipboard</>}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-text-muted flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-text-muted italic" data-testid="client-access-empty">
          No emails bound yet. Add one above to invite the owner.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="client-access-list">
          {rows.map((r) => {
            const s = stateLabel(r);
            return (
              <li key={r.email}
                  className="flex items-center gap-3 border border-border rounded-md px-3 py-2 text-xs"
                  data-testid={`client-access-row-${r.email}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-text-primary truncate">{r.email}</div>
                  {r.bound_at && (
                    <div className="text-text-muted mt-0.5">
                      Bound {new Date(r.bound_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded border ${s.tone} whitespace-nowrap`}>
                  {s.text}
                </span>
                <button
                  type="button"
                  onClick={() => revoke(r.email)}
                  disabled={busy}
                  title={`Revoke ${r.email}`}
                  aria-label={`Revoke ${r.email}`}
                  data-testid={`client-access-revoke-${r.email}`}
                  className="text-state-error hover:bg-red-50 p-1 rounded"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ClientAccessSection;
